// src/api/healthThreatsApi.js

const DEFAULT_BASE = "http://localhost:8000";

export function getApiBase() {
  // CRA exposes env vars with REACT_APP_* at build-time
  const configured = (process.env.REACT_APP_API_BASE_URL || "").trim();
  if (configured) return configured;

  // No explicit base configured (prod build): call the API through this
  // same origin's nginx proxy (/api/*), so browser requests never cross
  // from https -> http and get blocked as mixed content.
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/api`;
  }

  return DEFAULT_BASE;
}

export function buildOutputFileUrl({
  country,
  threat,
  term = "Risk_Factor",
  library = "Plotly",
  file_type = "json",
}) {
  const base = getApiBase().replace(/\/+$/, "");
  const url = new URL(`${base}/outputs/file`);
  url.searchParams.set("country", country);
  url.searchParams.set("threat", threat);
  url.searchParams.set("term", term);
  url.searchParams.set("library", library);
  url.searchParams.set("file_type", file_type);
  return url.toString();
}

export async function fetchOutputFileBlob(params) {
  const url = buildOutputFileUrl(params);
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return await res.blob();
}

export async function fetchOutputFileJson(params) {
  const blob = await fetchOutputFileBlob({ ...params, file_type: "json" });
  const text = await blob.text();
  return JSON.parse(text);
}

// FastAPI returns `detail` as either a plain string (our own errors, e.g.
// keycloak_admin.KeycloakAdminError) or, for pydantic validation errors
// (422), an array of {loc, msg, type} objects. Turn either into one
// readable sentence for the UI.
function extractApiErrorMessage(data, fallback) {
  const detail = data?.detail;

  if (!detail) return fallback;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        const msg = item?.msg || "Invalid value";
        return field ? `${field}: ${msg}` : msg;
      })
      .join(" ");
  }

  return fallback;
}

export async function registerUser({ username, email, password, role }) {
  const base = getApiBase().replace(/\/+$/, "");
  const res = await fetch(`${base}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, email, password, role }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(extractApiErrorMessage(data, `Registration failed (${res.status})`));
  }

  return data;
}
